<template>
  <v-menu v-model="dialog" :close-on-content-click="false" offset-y>
    <template #activator="{ on }">
      <v-btn
        v-if="!select"
        class="no-text-transform"
        outlined
        :ripple="false"
        style="background-color: #f5f7fa;"
        text
        v-on="on"
      >
        <app-icon class="pr-2" color="#4d4d4d">event</app-icon>
        <span class="activator-button">{{ activatorButtonText }}</span>
        <app-icon class="pl-2" color="#4d4d4d">expand_more</app-icon>
      </v-btn>
      <v-select
        v-else
        class="activator"
        :menu-props="{ maxHeight: 0, zIndex: -10 }"
        :items="[minimumDate, maximumDate]"
        :value="minimumDate || maximumDate"
        chips
        :label="labelText"
        multiple
        outlined
        dense
        hide-details
        v-on="on"
      >
        <template #selection="{ index }">
          <v-chip v-if="index === 0" small>
            <span>{{ formattedDateRange }}</span>
          </v-chip>
        </template>
      </v-select>
    </template>
    <v-card>
      <v-row class="flex-column px-5" no-gutters>
        <v-col>
          <v-row>
            <v-col>
              <div class="font-weight-bold label pb-1">From</div>
              <v-menu v-model="minDateMenu" :close-on-content-click="false" offset-y>
                <template #activator="{ on: minDateOn }">
                  <v-text-field
                    v-model="minimumDateFormatted"
                    clearable
                    dense
                    :error-messages="minimumErrorMessages"
                    :hint="`${uiDateFormat} format`"
                    outlined
                    persistent-hint
                    prepend-inner-icon="event"
                    v-on="minDateOn"
                    @input="onMinimumTextChange"
                    @click:prepend-inner="minDateMenu = !minDateMenu"
                  />
                </template>
                <v-date-picker
                  v-model="minimumDate"
                  :min="minSelectableDate"
                  :max="maxForMinimumDate"
                  no-title
                  scrollable
                  :type="datePickerType"
                  @input="onMinimumPickerChange"
                />
              </v-menu>
            </v-col>
            <v-col>
              <div class="font-weight-bold label pb-1">To</div>
              <v-menu v-model="maxDateMenu" :close-on-content-click="false" offset-y>
                <template #activator="{ on: maxDateOn }">
                  <v-text-field
                    ref="maxDateTextField"
                    v-model="maximumDateFormatted"
                    clearable
                    dense
                    :error-messages="maximumErrorMessages"
                    :hint="`${uiDateFormat} format`"
                    outlined
                    persistent-hint
                    prepend-inner-icon="event"
                    v-on="maxDateOn"
                    @input="onMaximumTextChange"
                  />
                </template>
                <v-date-picker
                  v-model="maximumDate"
                  :min="minForMaximumDate"
                  :max="maxSelectableDate"
                  no-title
                  scrollable
                  :type="datePickerType"
                  @input="onMaximumPickerChange"
                  @click:prepend-inner="maxDateMenu = !maxDateMenu"
                />
              </v-menu>
            </v-col>
          </v-row>
        </v-col>
        <v-col v-if="showQuickOptions" class="pb-4">
          <v-row class="flex-column" no-gutters align-content="center">
            <v-col v-if="showTodayQuickOption" cols="auto" class="pt-1">
              <app-button class="quick-button" @click="selectToday">Today</app-button>
            </v-col>
            <v-col v-if="showNext7DaysQuickOption" cols="auto" class="pt-1">
              <app-button class="quick-button" @click="selectNext7Days">Next 7 Days</app-button>
            </v-col>
            <v-col v-if="showNext14DaysQuickOption" cols="auto" class="pt-1">
              <app-button class="quick-button" @click="selectNext14Days">Next 14 Days</app-button>
            </v-col>
            <v-col v-if="showPastMonthQuickOption" cols="auto" class="pt-1">
              <app-button class="quick-button" @click="selectPastMonth">Past Month</app-button>
            </v-col>
            <v-col v-if="showPast6MonthsQuickOption" cols="auto" class="pt-1">
              <app-button class="quick-button" @click="selectPast6Months">Past 6 Months</app-button>
            </v-col>
            <v-col v-if="showPast12MonthsQuickOption" cols="auto" class="pt-1">
              <app-button class="quick-button" @click="selectPast12Months"
                >Past 12 Months</app-button
              >
            </v-col>
            <v-col v-if="showAllTimeQuickOption" cols="auto" class="pt-1">
              <app-button class="quick-button" @click="selectAllTime">All Time</app-button>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card>
  </v-menu>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { helpers } from 'vuelidate/lib/validators'
import moment from 'moment'
import _ from 'lodash'
import { MM_DD_YYYY, YYYY_MM_DD } from '@/helpers/date-helper'

export default {
  mixins: [validationMixin],
  props: {
    model: { type: Object, default: () => {} },
    internalDateFormat: { type: String, default: YYYY_MM_DD },
    uiDateFormat: { type: String, default: MM_DD_YYYY },
    uiDateValidationRegex: { type: RegExp, default: () => /^\d{1,2}\/\d{1,2}\/\d{4}$/ },
    datePickerType: { type: String, default: 'date' },
    minSelectableDate: { type: String, default: null },
    maxSelectableDate: { type: String, default: null },
    quickOptions: { type: Array, default: () => ['TODAY', 'PAST_6_MONTHS', 'PAST_12_MONTHS'] },
    labelText: { type: String, default: 'All time' },
    select: { type: Boolean, default: false }
  },
  data() {
    return {
      dialog: false,
      minimumDate: null,
      minimumDateFormatted: null,
      minDateMenu: false,
      maximumDate: null,
      maximumDateFormatted: null,
      maxDateMenu: false
    }
  },
  computed: {
    showQuickOptions() {
      return !_.isEmpty(this.quickOptions)
    },
    showTodayQuickOption() {
      return _.includes(this.quickOptions, 'TODAY')
    },
    showPastMonthQuickOption() {
      return _.includes(this.quickOptions, 'PAST_MONTH')
    },
    showPast6MonthsQuickOption() {
      return _.includes(this.quickOptions, 'PAST_6_MONTHS')
    },
    showPast12MonthsQuickOption() {
      return _.includes(this.quickOptions, 'PAST_12_MONTHS')
    },
    showNext7DaysQuickOption() {
      return _.includes(this.quickOptions, 'NEXT_7_DAYS')
    },
    showNext14DaysQuickOption() {
      return _.includes(this.quickOptions, 'NEXT_14_DAYS')
    },
    showAllTimeQuickOption() {
      return _.includes(this.quickOptions, 'ALL_TIME')
    },
    formattedDateRange() {
      return `${this.minimumDateFormatted ? this.minimumDateFormatted : ''} \u2014 ${
        this.maximumDateFormatted ? this.maximumDateFormatted : ''
      }`
    },
    activatorButtonText() {
      if (!this.minimumDateFormatted && !this.maximumDateFormatted) {
        return this.labelText
      }
      return this.formattedDateRange
    },
    maximumErrorMessages() {
      const errors = []
      if (!this.$v.maximumDateFormatted.$dirty) {
        return errors
      }
      !this.$v.maximumDateFormatted.validFormat && errors.push('Please use correct date format')
      !this.$v.maximumDateFormatted.validDate && errors.push('Please enter valid date')
      return errors
    },
    minimumErrorMessages() {
      const errors = []
      if (!this.$v.minimumDateFormatted.$dirty) {
        return errors
      }
      !this.$v.minimumDateFormatted.validFormat && errors.push('Please use correct date format')
      !this.$v.minimumDateFormatted.validDate && errors.push('Please enter valid date')
      return errors
    },
    maxForMinimumDate() {
      return this.maximumDate ? this.maximumDate : null
    },
    minForMaximumDate() {
      return this.minimumDate ? this.minimumDate : null
    }
  },
  watch: {
    model() {
      if (!_.isEmpty(this.model.min)) {
        this.minimumDate = moment(this.model.min).format(this.internalDateFormat)
        this.minimumDateFormatted = moment(this.model.min).format(this.uiDateFormat)
      } else {
        this.minimumDate = null
        this.minimumDateFormatted = null
      }
      if (!_.isEmpty(this.model.max)) {
        this.maximumDate = moment(this.model.max).format(this.internalDateFormat)
        this.maximumDateFormatted = moment(this.model.max).format(this.uiDateFormat)
      } else {
        this.maximumDate = null
        this.maximumDateFormatted = null
      }
    }
  },
  mounted() {
    if (!_.isEmpty(this.model.min)) {
      this.minimumDate = moment(this.model.min).format(this.internalDateFormat)
      this.minimumDateFormatted = moment(this.model.min).format(this.uiDateFormat)
    }
    if (!_.isEmpty(this.model.max)) {
      this.maximumDate = moment(this.model.max).format(this.internalDateFormat)
      this.maximumDateFormatted = moment(this.model.max).format(this.uiDateFormat)
    }
    this.emitInput()
  },
  methods: {
    onMaximumTextChange() {
      this.$v.maximumDateFormatted.$touch()
      if (!this.$v.maximumDateFormatted.$invalid) {
        this.maximumDate =
          this.maximumDateFormatted === null || this.maximumDateFormatted === ''
            ? null
            : moment(this.maximumDateFormatted, this.uiDateFormat)
                .endOf('day')
                .format(this.internalDateFormat)
        this.$emit('input:maximumDate', this.maximumDate)
        this.emitInput()
      }
    },
    onMinimumTextChange() {
      this.$v.minimumDateFormatted.$touch()
      if (!this.$v.minimumDateFormatted.$invalid) {
        this.minimumDate =
          this.minimumDateFormatted === null || this.minimumDateFormatted === ''
            ? null
            : moment(this.minimumDateFormatted, this.uiDateFormat)
                .startOf('day')
                .format(this.internalDateFormat)
        this.$emit('input:minimumDate', this.minimumDate)
        this.emitInput()
      }
      this.$refs.maxDateTextField.focus()
      this.maxDateMenu = true
    },
    onMaximumPickerChange(newValue) {
      const maxDate = moment(newValue).endOf('day')
      this.maximumDate = maxDate.format(this.internalDateFormat)
      this.maximumDateFormatted = maxDate.format(this.uiDateFormat)
      this.$emit('input:maximumDate', this.maximumDate)
      this.emitInput()
      this.maxDateMenu = false
    },
    onMinimumPickerChange(newValue) {
      const minDate = moment(newValue).startOf('day')
      this.minimumDate = minDate.format(this.internalDateFormat)
      this.minimumDateFormatted = minDate.format(this.uiDateFormat)
      this.$emit('input:minimumDate', this.minimumDate)
      this.emitInput()
      this.minDateMenu = false
      this.$refs.maxDateTextField.focus()
      this.maxDateMenu = true
    },
    selectLast(amount, unit) {
      const max = moment()
        .subtract(1, unit)
        .endOf(unit)
      this.maximumDate = max.format(this.internalDateFormat)
      this.maximumDateFormatted = max.format(this.uiDateFormat)
      this.$emit('input:maximumDate', this.maximumDate)
      this.emitInput()

      const min = moment()
        .subtract(amount, unit)
        .startOf(unit)
      this.minimumDate = min.format(this.internalDateFormat)
      this.minimumDateFormatted = min.format(this.uiDateFormat)
      this.$emit('input:minimumDate', this.minimumDate)
      this.emitInput()
    },
    selectNext(amount, unit, includeToday = true) {
      const max = moment()
        .add(amount, unit)
        .endOf(unit)
      this.maximumDate = max.format(this.internalDateFormat)
      this.maximumDateFormatted = max.format(this.uiDateFormat)
      this.$emit('input:maximumDate', this.maximumDate)
      this.emitInput()

      const min = moment()
        .add(includeToday ? 0 : 1, unit)
        .startOf(unit)
      this.minimumDate = min.format(this.internalDateFormat)
      this.minimumDateFormatted = min.format(this.uiDateFormat)
      this.$emit('input:minimumDate', this.minimumDate)
      this.emitInput()
    },
    emitInput() {
      this.$emit('input', { min: this.minimumDate, max: this.maximumDate })
    },
    selectPastMonth() {
      this.selectLast(1, 'month')
    },
    selectPast6Months() {
      this.selectLast(6, 'months')
    },
    selectPast12Months() {
      this.selectLast(12, 'months')
    },
    selectNext7Days() {
      this.selectNext(7, 'days')
    },
    selectNext14Days() {
      this.selectNext(14, 'days')
    },
    selectAllTime() {
      this.maximumDate = null
      this.maximumDateFormatted = null
      this.$emit('input:maximumDate', this.maximumDate)
      this.emitInput()

      this.minimumDate = null
      this.minimumDateFormatted = null
      this.$emit('input:minimumDate', this.minimumDate)
      this.emitInput()
    },
    selectToday() {
      const max = moment().endOf('day')
      this.maximumDate = max.format(this.internalDateFormat)
      this.maximumDateFormatted = max.format(this.uiDateFormat)
      this.$emit('input:maximumDate', this.maximumDate)
      this.emitInput()

      const min = moment().startOf('day')
      this.minimumDate = min.format(this.internalDateFormat)
      this.minimumDateFormatted = min.format(this.uiDateFormat)
      this.$emit('input:minimumDate', this.minimumDate)
      this.emitInput()
    }
  },
  validations() {
    return {
      maximumDateFormatted: {
        validFormat: helpers.regex(null, this.uiDateValidationRegex),
        validDate: value => _.isEmpty(value) || moment(value, this.uiDateFormat).isValid
      },
      minimumDateFormatted: {
        validFormat: helpers.regex(null, this.uiDateValidationRegex),
        validDate: value => _.isEmpty(value) || moment(value, this.uiDateFormat).isValid
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.activator-button {
  color: var(--v-dark-base);
}

.label {
  font-size: 12px;
}

.no-text-transform {
  text-transform: none;
}

.quick-button {
  width: 330px;
}
</style>
